import React from "react";
import {
    Controller,
    Control,
    RegisterOptions,
    FieldValues,
} from "react-hook-form";
import { useAppSelector } from '../../../../store/hooks/StoreHooks';

interface RadioWithInputProps {
    name: string;
    radioName: string;
    label?: string;
    placeholder?: string;
    className?: string;
    control: Control<FieldValues>;
    rules?: RegisterOptions;
    error?: string;
    options?: { label: string; value: string; default: boolean }[];
    unit?: string;
    onInputChange: (value: any) => void;
    onRadioChange: (value: any) => void;
    onInputBlur?: (value: any) => void;
    radioValue?: number | string;
    inputValue?: number | string;
    inputLabel?: string;
    required?: boolean;
    checked?: boolean;
}

const RadioWithInput: React.FC<RadioWithInputProps> = ({
    name,
    radioName,
    label,
    placeholder,
    className,
    control,
    rules,
    error,
    options = [
        { label: "Option 1", value: "option1", default: false },
        { label: "Option 2", value: "option2", default: false },
    ],
    unit,
    onInputChange,
    onRadioChange,
    onInputBlur,
    radioValue,
    inputValue,
    inputLabel,
    required,
    checked,
}) => {
    const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
    const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
    const isDisabled = (customerEnabled && !customerCreated) || projectReadOnly;

    // Simple input filtering based on rules
    const filterInput = (inputValue: string): string => {
        if (!rules) return inputValue;

        const r: any = rules;
        let filteredValue = inputValue;

        // Handle allowDecimal rule
        if (r.allowDecimal === false) {
            // Only allow integers (digits and optional minus sign)
            if (filteredValue.startsWith('-')) {
                filteredValue = '-' + filteredValue.substring(1).replace(/[^0-9]/g, '');
            } else {
                filteredValue = filteredValue.replace(/[^0-9]/g, '');
            }
        } else if (r.allowDecimal === true) {
            // Allow decimals but ensure valid format
            filteredValue = filteredValue.replace(/[^0-9.]/g, '');

            // Ensure only one decimal point
            const decimalCount = (filteredValue.match(/\./g) || []).length;
            if (decimalCount > 1) {
                const firstDecimalIndex = filteredValue.indexOf('.');
                filteredValue = filteredValue.substring(0, firstDecimalIndex + 1) +
                    filteredValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
            }

            if (r.allowNegative === true) {
                const negativeValue = filteredValue.replace(/[^0-9.-]/g, '');
                // Ensure minus sign is only at the beginning
                if (negativeValue.includes('-') && negativeValue.indexOf('-') > 0) {
                    filteredValue = filteredValue.replace(/-/g, '');
                }
            }
        }

        // Handle maxLength
        if (typeof r.maxLength === 'number' && filteredValue.length > r.maxLength) {
            filteredValue = filteredValue.substring(0, r.maxLength);
        }

        return filteredValue;
    };

    // Enhanced onChange handler
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, fieldOnChange?: (value: any) => void) => {
        const inputValue = e.target.value;
        const filteredValue = filterInput(inputValue);

        if (filteredValue !== inputValue) {
            const syntheticEvent = { ...e, target: { ...e.target, value: filteredValue } };
            fieldOnChange?.(filteredValue);
            onInputChange?.(syntheticEvent);
        } else {
            fieldOnChange?.(inputValue);
            onInputChange?.(e);
        }
    };

    return (
        <div className={className ?? "flex flex-col gap-[6px]"}>
            {label && (
                <label
                    className={`flex justify-start items-start mb-1 ${isDisabled ? 'text-neutral-200' : 'text-neutral-25'}`}
                    htmlFor={name}
                >
                    {label}
                </label>
            )}

            {/* Radio Button Controller */}
            <Controller
                name={radioName}
                control={control}
                rules={rules}
                defaultValue={
                    (radioValue as any) ?? options.find((o) => o.default)?.value ?? ""
                }
                render={({ field: radioField }) => (
                    <div className="flex gap-4">
                        {options.map((option) => {
                            const fallbackDefault = options.find((o) => o.default)?.value;
                            const current = (radioValue ?? radioField.value ?? fallbackDefault) as any;
                            const isSelected = current === option.value;
                            const inputFieldName = `${name}_${option.value}`;

                            return (
                                <div key={option.value} className="flex flex-col gap-2 w-full">
                                    {/* Radio button */}
                                    <div className={`flex flex-row items-center gap-2`}>
                                        <input
                                            type="radio"
                                            value={option.value}
                                            checked={checked}
                                            onChange={(e) => {
                                                radioField.onChange(e.target.value);
                                                onRadioChange?.(e.target.value);
                                            }}
                                            disabled={isDisabled}
                                            className={`accent-primary-600 outline-none border border-darkcolor-300 bg-darkcolor-800 h-[20px] w-[20px] ${isSelected ? "ring-0 ring-primary-500" : ""
                                                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        />
                                        <label className={`flex items-center gap-2 ${isDisabled ? 'text-neutral-200' : 'text-neutral-25'}`}>
                                            {option.label}
                                            {(required || rules?.required) && (
                                                <span className="text-error-400 ml-1">*</span>
                                            )}
                                        </label>
                                    </div>

                                    {/* Input field bound to its own form field */}
                                    <Controller
                                        name={inputFieldName}
                                        control={control}
                                        rules={{
                                            ...rules,
                                            required: isSelected ? rules?.required : false,
                                        }}
                                        render={({ field: inputField }) => (
                                            <>
                                                {inputLabel && <label htmlFor={name} className={`flex justify-start items-start mb-1 text-sm ${isDisabled ? 'text-neutral-200' : 'text-neutral-25'}`}>
                                                    {inputLabel}
                                                    {(required || rules?.required) && <span className="text-error-400 ml-1">*</span>}
                                                </label>}
                                                <div
                                                    className={`flex items-center rounded-[8px] border w-full ${isSelected && !isDisabled || !checked
                                                        ? "border-darkcolor-400 hover:border-primary-500 focus-within:border-primary-500"
                                                        : "border-neutral-600 cursor-not-allowed"
                                                        } ${error
                                                            ? "border-error-500 focus-within:ring-0 focus-within:ring-error-600"
                                                            : ""
                                                        } ${(customerEnabled && !customerCreated) || projectReadOnly || !checked ? 'text-neutral-200' : ''}`}
                                                >
                                                    <input
                                                        {...inputField}
                                                        id={`${inputFieldName}-${option.value}`}
                                                        placeholder={placeholder}
                                                        type="text"
                                                        disabled={!checked || isDisabled}
                                                        aria-invalid={!!error}
                                                        className={`flex-grow max-h-[44px] min-h-[40px] bg-transparent px-3 focus:outline-none ${!checked || isDisabled
                                                            ? "text-neutral-200 cursor-not-allowed"
                                                            : "text-neutral-25"
                                                            }`}
                                                        onChange={(e) => {
                                                            handleInputChange(e, inputField.onChange);
                                                        }}
                                                        onBlur={(e) => {
                                                            inputField.onBlur();
                                                            if (isSelected && !isDisabled && onInputBlur) {
                                                                onInputBlur(e);
                                                            }
                                                        }}
                                                        value={inputValue || ""}
                                                    />
                                                    {unit && (
                                                        <span
                                                            className={`pr-3 whitespace-nowrap select-none ${((customerEnabled && !customerCreated) || projectReadOnly) ? "text-neutral-200" : "text-gray-400"}`}
                                                            style={{
                                                                color: !isSelected || isDisabled || !inputField.value
                                                                    ? "#E5E7EB"
                                                                    : "#FFFFFF",
                                                            }}
                                                        >
                                                            {unit}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            />

            {error && <span className="text-error-400 text-sm">{error}</span>}
        </div>
    );
};

export default RadioWithInput;
